
import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button, ButtonProps } from "@/components/ui/button";
import { LogIn, UserPlus } from "lucide-react";

interface SignInButtonProps extends ButtonProps {
  showIcon?: boolean;
  label?: string;
  redirect?: string;
  signup?: boolean;
  asChild?: boolean;
}

const SignInButton: React.FC<SignInButtonProps> = ({ 
  showIcon = true, 
  label = "Sign In", 
  redirect,
  signup = false,
  className,
  asChild = false,
  ...props 
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  
  // Create a generic click handler that works with both div and button elements
  const handleClick = () => {
    navigate('/auth', { 
      state: { 
        returnTo: redirect || location.pathname,
        signup
      } 
    });
  };
  
  // If this component is being used as a child of another interactive element
  // render a div instead of a button to prevent nesting issues
  if (asChild) {
    // Extract button-specific props that aren't compatible with div
    const {
      // Button-specific props to exclude
      form,
      formAction,
      formEncType,
      formMethod,
      formNoValidate,
      formTarget,
      name,
      type,
      value,
      disabled,
      // Any other button-specific props that could cause issues
      size,
      variant,
      ...safeProps
    } = props;
    
    // Use only HTML div attributes, casting to the correct type
    const divProps: React.HTMLAttributes<HTMLDivElement> = {
      ...safeProps as unknown as React.HTMLAttributes<HTMLDivElement>,
      onClick: handleClick,
      className,
      role: "button",
      tabIndex: 0
    };
    
    return (
      <div {...divProps}>
        {showIcon && (signup ? <UserPlus className="w-4 h-4 mr-2" /> : <LogIn className="w-4 h-4 mr-2" />)}
        {label}
      </div>
    );
  }
  
  return (
    <Button 
      onClick={handleClick} 
      className={className}
      {...props}
    >
      {showIcon && (signup ? <UserPlus className="w-4 h-4 mr-2" /> : <LogIn className="w-4 h-4 mr-2" />)}
      {label}
    </Button>
  );
};

export default SignInButton;
